import { useMemo } from "react";
import { useSelector } from "react-redux";

import { MakerIcon } from "@/assets/icons";
import EmptyPlaceholder from "@/components/emptyPlaceholder";
import { Divider } from "@/components/ui/Divider";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { RootState } from "@/store";
import {
  CatchmentTransportLabel,
  ECatchmentInputBy,
  ECatchmentType,
  ERoadAccessMeasurement,
  RoadExclusionsLabel
} from "@/types/spatial";
import { convertDatasetResponseToFeatureCollection } from "@/utils/dataset";

import { area } from "@turf/turf";

import { countTotalRows } from "../catchmentDataSection/catchmentDataset";
import LabelValueItem from "./labelValueItem";

const CatchmentSelected = () => {
  const { isBVTDataForCatchmentEnabled } = useAppFeatures();
  const { catchmentSettings } = useSelector(
    (state: RootState) => state.spatial
  );
  const totalPOIs =
    countTotalRows(catchmentSettings?.datasetConfig?.datasets) ?? 0;
  const hasCoordinateData =
    (catchmentSettings?.placeCoordinates &&
      catchmentSettings.placeCoordinates?.length > 0) ||
    totalPOIs > 0;

  const isByLocation =
    catchmentSettings?.catchmentInputBy === ECatchmentInputBy.BY_LOCATION;
  const isByDataset =
    catchmentSettings?.catchmentInputBy === ECatchmentInputBy.BY_DATASET;

  // Build polygon catchment list (name + area in km²) from saved
  // datasetConfig polygon datasets. Numbering matches the map label flow:
  // a single 1-based counter across all visible polygon datasets.
  const polygonCatchmentItems = useMemo(() => {
    if (!isBVTDataForCatchmentEnabled) return [];
    const datasets = catchmentSettings?.datasetConfig?.datasets ?? {};
    const items: { name: string; areaKm2: number }[] = [];
    let polygonIndex = 0;
    for (const entry of Object.values(datasets)) {
      if (entry.dataset?.data?.spatial_aggregation_type !== "polygon") continue;
      const featureCollection = convertDatasetResponseToFeatureCollection(
        entry.rawDataset
      );
      for (const feature of featureCollection.features) {
        if (
          feature.geometry?.type !== "Polygon" &&
          feature.geometry?.type !== "MultiPolygon"
        )
          continue;
        polygonIndex += 1;
        items.push({
          name: `Location layer polygon #${polygonIndex}`,
          areaKm2: area(feature) / 1_000_000
        });
      }
    }
    return items;
  }, [
    isBVTDataForCatchmentEnabled,
    catchmentSettings?.datasetConfig?.datasets
  ]);
  const isPolygonDatasetCatchment =
    isBVTDataForCatchmentEnabled &&
    isByDataset &&
    polygonCatchmentItems.length > 0;

  const byRadius =
    catchmentSettings?.catchmentType === ECatchmentType.BY_RADIUS;
  const byRoadAccess =
    catchmentSettings?.catchmentType === ECatchmentType.BY_ROAD;

  const isDistance =
    catchmentSettings?.roadAccessConfig?.measurementType ===
    ERoadAccessMeasurement.DISTANCE;

  const onGetRoadExclusionValue = () => {
    if (!catchmentSettings?.roadAccessConfig?.roadExclusions) return "-";
    if (
      catchmentSettings?.roadAccessConfig?.roadExclusions &&
      !catchmentSettings?.roadAccessConfig?.roadExclusions.length
    )
      return "-";
    return catchmentSettings?.roadAccessConfig?.roadExclusions
      ?.map((item) => RoadExclusionsLabel[item])
      .join(", ");
  };

  return (
    <div>
      {hasCoordinateData ? (
        <div className="p-4 rounded-[12px] flex flex-col bg-white-smoke gap-2">
          {isByLocation && (
            <LabelValueItem label="Coordinate">
              {catchmentSettings.placeCoordinates?.map((item, index) => (
                <div key={item.id} className="flex flex-col">
                  <span className="text-txtHeading">
                    {item.locationName && item.locationName !== ""
                      ? item.locationName
                      : `Marker #${index + 1}`}
                  </span>
                  <span className="text-[10px]">
                    {item.lat}, {item.lng}
                  </span>
                </div>
              ))}
            </LabelValueItem>
          )}
          {isByDataset && !isPolygonDatasetCatchment && (
            <LabelValueItem label="Total POI">
              <span className="text-txtHeading">
                {totalPOIs} List of markers
              </span>
            </LabelValueItem>
          )}
          {isPolygonDatasetCatchment && (
            <LabelValueItem label="Polygon">
              {polygonCatchmentItems.map((item) => (
                <div key={item.name} className="flex flex-col">
                  <span className="text-txtHeading">{item.name}</span>
                  <span className="text-[10px]">
                    {item.areaKm2.toFixed(2)} km²
                  </span>
                </div>
              ))}
            </LabelValueItem>
          )}
          {!isPolygonDatasetCatchment && (
            <Divider className="bg-gainsboro w-[100%] h-[1px]" />
          )}
          {!isPolygonDatasetCatchment && byRadius && (
            <>
              <LabelValueItem label="Catchment Type" value="By Radius" />
              <LabelValueItem label="Radius">
                {catchmentSettings.radiusData?.map((item) => (
                  <div key={item} className="flex flex-col">
                    <span className="text-txtHeading">{item} m</span>
                  </div>
                ))}
              </LabelValueItem>
            </>
          )}
          {!isPolygonDatasetCatchment && byRoadAccess && (
            <>
              <LabelValueItem label="Catchment Type" value="By Road Access" />
              <LabelValueItem
                label="Routing"
                value={
                  CatchmentTransportLabel[
                    catchmentSettings.roadAccessConfig?.routingType ?? "default"
                  ]
                }
              />
              <LabelValueItem label="Road Exclusions ">
                <span className="text-txtHeading">
                  {onGetRoadExclusionValue()}
                </span>
              </LabelValueItem>
              {isDistance ? (
                <>
                  <LabelValueItem label="Measurement" value="Distance" />
                  <LabelValueItem label="Distance in Meter">
                    {catchmentSettings.roadAccessConfig?.distances?.map(
                      (item) => (
                        <div key={item} className="flex flex-col">
                          <span className="text-txtHeading">{item} m</span>
                        </div>
                      )
                    )}
                  </LabelValueItem>
                </>
              ) : (
                <>
                  <LabelValueItem label="Measurement" value="Duration" />
                  <LabelValueItem label="Duration in Minute">
                    {catchmentSettings.roadAccessConfig?.durations?.map(
                      (item) => (
                        <div key={item} className="flex flex-col">
                          <span className="text-txtHeading">
                            {item} minutes
                          </span>
                        </div>
                      )
                    )}
                  </LabelValueItem>
                </>
              )}
            </>
          )}
        </div>
      ) : (
        <EmptyPlaceholder
          className="my-2 px-4 text-xs"
          icon={
            <MakerIcon className="fill-light-gray" width={21} height={26} />
          }
          description="No catchment areas yet. Set one up to start managing coverage."
        />
      )}
    </div>
  );
};

export default CatchmentSelected;
